const PRECISION = 6;
const SCALE_FACTOR = 10 ** PRECISION;
const BASE36_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

function encodeBase36(value: bigint): string {
  let result = '';
  let num = value;
  for (let i = 0; i < 12; i++) {
    result = BASE36_CHARS[Number(num % 36n)] + result;
    num = num / 36n;
  }
  return result.padStart(12, 'A');
}

function decodeBase36(str: string): bigint {
  let result = 0n;
  for (const ch of str) {
    const idx = BASE36_CHARS.indexOf(ch);
    if (idx === -1) throw new Error(`Invalid Base36 character: ${ch}`);
    result = result * 36n + BigInt(idx);
  }
  return result;
}

export function coordsToFrequency(lat: number, lng: number): string {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return 'N/A';
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return 'N/A';
  const latInt = Math.round((lat + 90) * SCALE_FACTOR);
  const lngInt = Math.round((lng + 180) * SCALE_FACTOR);
  const combined = (BigInt(latInt) << 32n) | BigInt(lngInt);
  const encoded = encodeBase36(combined);
  return `${encoded.slice(0, 3)}-${encoded.slice(3, 6)}-${encoded.slice(6, 9)}-${encoded.slice(9, 12)}`;
}

export function frequencyToCoords(frequency: string): { lat: number; lng: number } | null {
  if (!frequency || typeof frequency !== 'string') return null;
  const parts = frequency.trim().split('-');
  if (parts.length !== 4 || parts.some(p => p.length !== 3)) return null;
  const encoded = parts.join('');
  if ([...encoded].some(ch => !BASE36_CHARS.includes(ch))) return null;
  try {
    const combined = decodeBase36(encoded);
    const latInt = Number(combined >> 32n);
    const lngInt = Number(combined & 0xffffffffn);
    const lat = parseFloat((latInt / SCALE_FACTOR - 90).toFixed(PRECISION));
    const lng = parseFloat((lngInt / SCALE_FACTOR - 180).toFixed(PRECISION));
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
  } catch {
    return null;
  }
}

export function calcDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLng = (lng2 - lng1) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}
